import type { Task, TaskStatus } from '@obras/shared';
import { TASK_URGENCY_LABELS } from '@obras/shared';
import { CalendarDays, ListChecks, MessageSquare } from 'lucide-react';
import { AnimatePresence, LayoutGroup, motion, type Variants } from 'motion/react';
import { useId } from 'react';

import { EASE_OUT_QUINT } from '../../components/motion.js';
import { Card } from '../../components/ui/Card.js';
import { Avatar, Skeleton } from '../../components/ui/Feedback.js';
import { cn } from '../../lib/cn.js';
import {
  APARENCIA_DA_PRIORIDADE,
  COR_DO_PRAZO,
  lerPrazo,
  ORDEM_DAS_SECOES,
  ordenarTarefas,
  TITULO_DA_SECAO,
} from './aparencia.js';
import { useAtualizarTarefa } from './api.js';
import { IconeDaSituacao, MenuDaSituacao } from './Situacao.js';

/** Até onde a fila de entrada vai: da nona em diante, todas juntas. */
const ESCALONADAS = 8;

const LINHA: Variants = {
  hidden: { opacity: 0, y: 8 },
  shown: (indice: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.32,
      ease: EASE_OUT_QUINT,
      delay: Math.min(indice, ESCALONADAS) * 0.03,
    },
  }),
  gone: { opacity: 0, transition: { duration: 0.14, ease: EASE_OUT_QUINT } },
};

/**
 * A lista de tarefas (design.md §8, Tarefas): uma seção por situação, na ordem
 * de `ORDEM_DAS_SECOES`, só das situações pedidas — a aba "Em aberto" pede as
 * três abertas; a "Concluídas", só a concluída, e aí a seção não tem
 * cabeçalho: o nome e a contagem já estão na aba. Seção vazia não aparece.
 *
 * Trocar a situação pela linha leva a tarefa para a outra seção deslizando:
 * é a mesma linha, no lugar novo (`layoutId`), e não uma que some e outra que
 * nasce. A que vai para outra aba sai da lista apagando.
 */
export function ListaDeTarefas({
  tarefas,
  hoje,
  situacoes,
  onAbrir,
}: {
  tarefas: readonly Task[];
  hoje: string;
  situacoes: readonly TaskStatus[];
  onAbrir: (id: string) => void;
}) {
  const atualizar = useAtualizarTarefa();

  const secoes = ORDEM_DAS_SECOES.filter((status) => situacoes.includes(status))
    .map((status) => ({
      status,
      tarefas: ordenarTarefas(
        tarefas.filter((tarefa) => tarefa.status === status),
        status,
      ),
    }))
    .filter((secao) => secao.tarefas.length > 0);

  return (
    <LayoutGroup>
      <div className="flex flex-col gap-7">
        <AnimatePresence initial={false}>
          {secoes.map((secao) => (
            <Secao
              key={secao.status}
              status={secao.status}
              tarefas={secao.tarefas}
              hoje={hoje}
              comCabecalho={situacoes.length > 1}
              onAbrir={onAbrir}
              onTrocarSituacao={(id, status) => atualizar.mutate({ id, status })}
            />
          ))}
        </AnimatePresence>
      </div>
    </LayoutGroup>
  );
}

function Secao({
  status,
  tarefas,
  hoje,
  comCabecalho,
  onAbrir,
  onTrocarSituacao,
}: {
  status: TaskStatus;
  tarefas: readonly Task[];
  hoje: string;
  comCabecalho: boolean;
  onAbrir: (id: string) => void;
  onTrocarSituacao: (id: string, status: TaskStatus) => void;
}) {
  const tituloId = useId();

  return (
    <motion.section
      layout="position"
      exit={{ opacity: 0, transition: { duration: 0.14, ease: EASE_OUT_QUINT } }}
      {...(comCabecalho
        ? { 'aria-labelledby': tituloId }
        : { 'aria-label': TITULO_DA_SECAO[status] })}
    >
      {comCabecalho ? (
        <header className="mb-2 flex items-center gap-2">
          <IconeDaSituacao status={status} size={16} />
          <h2 id={tituloId} className="text-[13px] font-semibold tracking-tight">
            {TITULO_DA_SECAO[status]}
          </h2>
          <span className="tabular text-[12px] text-ink-3">{tarefas.length}</span>
        </header>
      ) : null}

      <Card square>
        <motion.ul initial="hidden" animate="shown">
          <AnimatePresence initial={false} mode="popLayout">
            {tarefas.map((tarefa, indice) => (
              <LinhaDaTarefa
                key={tarefa.id}
                tarefa={tarefa}
                indice={indice}
                hoje={hoje}
                onAbrir={onAbrir}
                onTrocarSituacao={onTrocarSituacao}
              />
            ))}
          </AnimatePresence>
        </motion.ul>
      </Card>
    </motion.section>
  );
}

/**
 * A linha: a situação (que troca pelo menu), o título e, à direita, o que diz
 * do andamento — o checklist, os comentários, a prioridade fora do normal, o
 * prazo e a equipe —, em colunas de largura fixa, alinhadas de uma linha para a
 * outra. No celular, esses dados descem para baixo do título.
 *
 * A linha inteira abre a tarefa; o título é o botão de verdade, para o
 * teclado.
 */
function LinhaDaTarefa({
  tarefa,
  indice,
  hoje,
  onAbrir,
  onTrocarSituacao,
}: {
  tarefa: Task;
  indice: number;
  hoje: string;
  onAbrir: (id: string) => void;
  onTrocarSituacao: (id: string, status: TaskStatus) => void;
}) {
  const concluida = tarefa.status === 'concluida';

  return (
    <motion.li
      layout="position"
      layoutId={tarefa.id}
      variants={LINHA}
      custom={indice}
      exit="gone"
      transition={{ layout: { duration: 0.3, ease: EASE_OUT_QUINT } }}
      className="border-t border-line first:border-t-0"
    >
      <div
        onClick={() => onAbrir(tarefa.id)}
        className="flex cursor-pointer items-start gap-2 px-2 py-2 transition-colors duration-100 hover:bg-sunken sm:items-center sm:gap-3 sm:px-3"
      >
        <MenuDaSituacao
          status={tarefa.status}
          onChange={(status) => onTrocarSituacao(tarefa.id, status)}
        />

        <div className="min-w-0 flex-1 py-1.5 sm:py-0">
          <button
            type="button"
            onClick={(evento) => {
              evento.stopPropagation();
              onAbrir(tarefa.id);
            }}
            className={cn(
              'block max-w-full truncate text-left text-[14px] font-medium tracking-tight',
              concluida && 'text-ink-3 line-through decoration-ink-3/50',
            )}
          >
            {tarefa.title}
          </button>
          <Dados
            tarefa={tarefa}
            hoje={hoje}
            className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 sm:hidden"
          />
        </div>

        <Dados tarefa={tarefa} hoje={hoje} className="hidden shrink-0 sm:flex" colunas />
      </div>
    </motion.li>
  );
}

/** Os dados da linha. Em colunas (`colunas`), cada um tem largura fixa, mesmo vazio. */
function Dados({
  tarefa,
  hoje,
  className,
  colunas = false,
}: {
  tarefa: Task;
  hoje: string;
  className?: string | undefined;
  colunas?: boolean | undefined;
}) {
  const feitos = tarefa.checklist.filter((item) => item.isDone).length;
  const total = tarefa.checklist.length;
  const prioridade = APARENCIA_DA_PRIORIDADE[tarefa.urgency];
  const prazo = tarefa.dueAt ? lerPrazo(tarefa.dueAt, hoje, tarefa.status === 'concluida') : null;

  const coluna = (largura: string) => (colunas ? largura : '');

  return (
    <div className={cn('items-center text-[12px] text-ink-3', className)}>
      {total > 0 || colunas ? (
        <span
          className={cn('flex items-center gap-1.5', coluna('w-14'))}
          title={total ? `${feitos} de ${total} itens feitos` : undefined}
        >
          {total > 0 ? (
            <>
              <ListChecks
                size={14}
                strokeWidth={1.75}
                aria-hidden
                className={feitos === total ? 'text-success' : undefined}
              />
              <span className="tabular">
                {feitos}/{total}
              </span>
            </>
          ) : null}
        </span>
      ) : null}

      {tarefa.commentCount > 0 || colunas ? (
        <span
          className={cn('flex items-center gap-1.5', coluna('w-11'))}
          title={
            tarefa.commentCount
              ? tarefa.commentCount === 1
                ? '1 comentário'
                : `${tarefa.commentCount} comentários`
              : undefined
          }
        >
          {tarefa.commentCount > 0 ? (
            <>
              <MessageSquare size={14} strokeWidth={1.75} aria-hidden />
              <span className="tabular">{tarefa.commentCount}</span>
              <span className="sr-only">
                {tarefa.commentCount === 1 ? 'comentário' : 'comentários'}
              </span>
            </>
          ) : null}
        </span>
      ) : null}

      {prioridade || colunas ? (
        <span className={cn('flex items-center gap-1.5', coluna('w-[76px]'), prioridade?.cor)}>
          {prioridade ? (
            <>
              <prioridade.icone size={14} strokeWidth={1.75} aria-hidden />
              {TASK_URGENCY_LABELS[tarefa.urgency]}
            </>
          ) : null}
        </span>
      ) : null}

      {prazo || colunas ? (
        <span
          className={cn(
            'flex items-center gap-1.5',
            coluna('w-[76px]'),
            prazo && COR_DO_PRAZO[prazo.tom],
          )}
          title={prazo?.vencido ? 'Prazo vencido' : prazo ? 'Prazo' : undefined}
        >
          {prazo ? (
            <>
              <CalendarDays size={14} strokeWidth={1.75} aria-hidden />
              <span className="tabular">{prazo.texto}</span>
            </>
          ) : null}
        </span>
      ) : null}

      <Pessoas tarefa={tarefa} className={coluna('w-[76px] justify-end')} />
    </div>
  );
}

/** Quem participa: até três avatares sobrepostos e o "+N" do resto. */
function Pessoas({ tarefa, className }: { tarefa: Task; className?: string | undefined }) {
  const visiveis = tarefa.members.slice(0, 3);
  const resto = tarefa.members.length - visiveis.length;

  return (
    <span
      className={cn('flex items-center', className)}
      title={tarefa.members.map((pessoa) => pessoa.name).join(', ')}
    >
      {/* O primeiro por cima: as iniciais dele inteiras, as dos seguintes mal tocadas. */}
      <span className="flex -space-x-1">
        {visiveis.map((pessoa, indice) => (
          <span
            key={pessoa.id}
            className="relative rounded-full ring-2 ring-surface"
            style={{ zIndex: visiveis.length - indice }}
          >
            <Avatar name={pessoa.name} url={pessoa.avatarUrl} size={22} />
          </span>
        ))}
      </span>
      {resto > 0 ? <span className="tabular ml-1 text-[11px]">+{resto}</span> : null}
      <span className="sr-only">
        {tarefa.members.length === 1 ? '1 pessoa' : `${tarefa.members.length} pessoas`}
      </span>
    </span>
  );
}

/** A lista enquanto as tarefas não chegam: uma seção com as linhas na altura delas. */
export function ListaDeTarefasEsqueleto() {
  return (
    <div aria-hidden>
      <Skeleton className="mb-2 h-3.5 w-32 rounded-none" />
      <Card square>
        {Array.from({ length: 5 }, (_, linha) => (
          <div
            key={linha}
            className="flex items-center gap-3 border-t border-line px-3 py-3.5 first:border-t-0"
          >
            <Skeleton className="size-5 rounded-full" />
            <Skeleton className="h-3.5 flex-1 rounded-none" />
            <Skeleton className="hidden h-3.5 w-64 rounded-none sm:block" />
          </div>
        ))}
      </Card>
    </div>
  );
}
